import React from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Heart, Users, ShieldCheck, MessageCircle, MapPin, Facebook, Instagram, Twitter, Star, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { resolveImg } from "../lib/api";
import { PLAY_STORE_URL, PLAY_BADGE_FR } from "../components/AppBanner";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.07H2.18a11 11 0 0 0 0 9.87l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
);

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [recent, setRecent] = React.useState([]);
  const [openFaq, setOpenFaq] = React.useState(0);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles/recent");
        setRecent(data);
      } catch (e) {}
    })();
  }, []);

  const handleLogin = () => {
    if (user && user.onboarded) {
      navigate(!user.verified ? "/verification" : "/decouvrir");
      return;
    }
    // Utilisateur non connecté OU inscription non terminée : passage par Google
    // (select_account permet de choisir un autre compte existant)
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  React.useEffect(() => {
    if (!loading && user && user.onboarded && !location.state?.stay) {
      navigate(!user.verified ? "/verification" : "/decouvrir");
    }
  }, [user, loading, navigate, location.state]);

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(180deg, #F9F6F0 0%, #FCEFE3 38%, #F8DCC5 72%, #F3C79E 100%)" }}
    >
      {/* Header bar (same color as footer) */}
      <header className="text-white" style={{ backgroundColor: "#3b82f6" }} data-testid="landing-header">
        <div className="max-w-md md:max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full bg-kora-terracotta flex items-center justify-center shrink-0">
              <Heart size={18} className="text-white" fill="white" />
            </div>
            <span className="font-display font-bold text-xl sm:text-2xl text-white truncate">KoraLove</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <button
              data-testid="header-login-btn"
              onClick={handleLogin}
              className="text-sm font-medium text-white/90 hover:text-kora-ochre transition-colors px-2 sm:px-3 py-2 whitespace-nowrap"
            >
              Se connecter
            </button>
            <button
              data-testid="header-signup-btn"
              onClick={handleLogin}
              className="text-sm font-semibold bg-kora-terracotta text-white rounded-full px-4 sm:px-5 py-2.5 hover:bg-[#C94F2B] transition-colors whitespace-nowrap shrink-0"
            >
              S'inscrire
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-md md:max-w-5xl mx-auto px-6 pt-10 pb-16">
        <div className="md:grid md:grid-cols-2 md:gap-12 md:items-center">
          {/* Hero text */}
          <div className="animate-float-up">
            <span data-testid="hero-badge" className="inline-block text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 animate-badge-attention" style={{ backgroundColor: "#3b82f6" }}>
              Meilleur site de Rencontre pour la diaspora africaine
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-kora-ink">
              Trouvez l'amour ou l'amitié, <span className="text-gradient-warm">à votre façon</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-kora-ink/70 leading-relaxed">
              KoraLove réunit les Africains de la diaspora en <span className="font-semibold text-kora-sage">Europe</span>, aux
              <span className="font-semibold text-kora-terracotta"> USA</span>, en <span className="font-semibold text-kora-ochre">Chine</span>, au <span className="font-semibold text-kora-terracotta">Canada</span> et en <span className="font-semibold text-emerald-600">Afrique francophone</span> pour des rencontres amicales ou en vue d'un mariage. Simple, chaleureux et fait pour vous.
            </p>

            <div className="mt-6 flex flex-wrap gap-3" data-testid="hero-goal-pills">
              <span data-testid="hero-pill-mariage" className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-full animate-goal-pill" style={{ backgroundColor: "#3b82f6" }}>
                <Heart size={15} fill="currentColor" /> Rencontre pour Mariage
              </span>
              <span data-testid="hero-pill-amicale" className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-full animate-goal-pill" style={{ backgroundColor: "#3b82f6", animationDelay: "0.8s" }}>
                <Users size={15} /> Rencontre Amicale
              </span>
            </div>

            <button
              data-testid="hero-google-login-btn"
              onClick={handleLogin}
              className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-kora-terracotta hover:bg-[#C94F2B] transition-colors rounded-full px-7 py-4 font-bold text-white animate-kora-pulse"
            >
              <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0">
                <GoogleIcon />
              </span>
              S'inscrire Gratuitement
            </button>
            <p className="mt-3 text-xs text-kora-muted text-center md:text-left">Inscription rapide avec votre compte Google</p>

            <div className="mt-6 text-center md:text-left" data-testid="hero-playstore">
              <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" data-testid="hero-playstore-badge" className="inline-block">
                <img src={PLAY_BADGE_FR} alt="Disponible sur Google Play" className="h-16" />
              </a>
              <p className="mt-1 text-[11px] text-kora-muted max-w-xs mx-auto md:mx-0">
                Déjà inscrit(e) sur le site ? Connectez-vous dans l'application avec la <strong>même adresse email Google</strong> pour retrouver votre compte.
              </p>
            </div>
          </div>

          {/* Hero image */}
          <div className="mt-10 md:mt-0 animate-float-up" style={{ animationDelay: "0.1s" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(44,30,22,0.2)] aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/30677718/pexels-photo-30677718.jpeg"
                alt="Couple africain heureux"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-kora-ink/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-kora-terracotta flex items-center justify-center">
                  <Heart size={18} className="text-white" fill="white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-kora-ink">+1 200 célibataires</p>
                  <p className="text-xs text-kora-muted">vous attendent près de chez vous</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nouveaux membres — carrousel défilant */}
        {recent.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <h2 className="font-display font-bold text-lg text-kora-ink">Nouveaux membres</h2>
            </div>
            <div className="relative overflow-hidden" data-testid="recent-marquee">
              <div className="flex gap-4 w-max animate-marquee">
                {[...recent, ...recent].map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`recent-profile-${i}`}
                    onClick={handleLogin}
                    className="relative shrink-0 w-40 h-56 rounded-2xl overflow-hidden bg-gray-900 shadow-md hover:-translate-y-1 transition-transform"
                  >
                    <img src={resolveImg(p.photo_url)} alt={p.name} className="w-full h-full object-cover" draggable={false} />
                    <div className="absolute inset-0 bg-gradient-to-t from-kora-ink/85 via-transparent to-transparent" />
                    {p.verified && (
                      <span className="absolute top-2 right-2 bg-kora-sage text-white rounded-full p-1">
                        <ShieldCheck size={12} />
                      </span>
                    )}
                    <div className="absolute bottom-0 left-0 w-full p-3 text-white text-left">
                      <p className="font-display font-bold text-sm">{p.name}{p.age ? `, ${p.age}` : ""}</p>
                      <div className="flex items-center gap-1 text-white/80 text-xs">
                        <MapPin size={11} /> {p.residence_country}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Users, title: "Amical ou Mariage", desc: "Choisissez votre intention dès le départ." },
            { icon: MessageCircle, title: "Discutez librement", desc: "Matchez et échangez en toute simplicité." },
            { icon: ShieldCheck, title: "Sûr & respectueux", desc: "Profils 100% vérifiés." },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-3xl p-6 border border-kora-line shadow-[0_8px_30px_rgba(44,30,22,0.05)]">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: "#3b82f6" }}>
                <f.icon size={22} className="text-white" />
              </div>
              <h3 className="font-display font-bold text-lg text-kora-ink">{f.title}</h3>
              <p className="text-sm text-kora-ink/60 mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA inscription avant la vidéo */}
      <div className="max-w-md md:max-w-4xl mx-auto px-6 mt-14 text-center" data-testid="mid-signup-cta">
        <button
          data-testid="mid-google-login-btn"
          onClick={handleLogin}
          className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-kora-terracotta hover:bg-[#C94F2B] transition-colors rounded-full px-7 py-4 font-bold text-white animate-kora-pulse"
        >
          <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0">
            <GoogleIcon />
          </span>
          S'inscrire Gratuitement
        </button>
        <p className="mt-3 text-xs text-kora-muted">Inscription rapide avec votre compte Google</p>
      </div>

      {/* Vidéo tutorielle */}
      <section className="max-w-md md:max-w-4xl mx-auto px-6 mt-16" data-testid="tutorial-video-section">
        <div className="text-center mb-6">
          <p className="inline-flex items-center gap-2 text-kora-terracotta font-bold text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-kora-terracotta" /> Découvrez en vidéo
          </p>
          <h2 className="font-display font-black text-2xl md:text-3xl text-kora-ink mt-1">
            Comment s'inscrire en quelques secondes ?
          </h2>
          <p className="text-sm text-kora-ink/60 mt-2">1 minute pour tout comprendre</p>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(44,30,22,0.18)] border border-kora-line bg-black">
          <video
            data-testid="tutorial-video"
            controls
            playsInline
            preload="metadata"
            poster="/tutoriel-poster.jpg"
            className="w-full aspect-video"
          >
            <source src="/tutoriel-koralove.mp4" type="video/mp4" />
            Votre navigateur ne prend pas en charge la lecture vidéo.
          </video>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-5" data-testid="video-share-buttons">
          <span className="text-sm font-semibold text-kora-ink/70">Partagez la vidéo :</span>
          <a
            data-testid="share-whatsapp-btn"
            href={`https://wa.me/?text=${encodeURIComponent(`Découvre KoraLove, le site de rencontre de la diaspora africaine ❤️ Amitié ou mariage, inscription gratuite : ${window.location.origin}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold text-sm rounded-full px-5 py-2.5 hover:brightness-110 hover:-translate-y-0.5 transition-transform shadow-md"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
          <a
            data-testid="share-facebook-btn"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#1877F2] text-white font-semibold text-sm rounded-full px-5 py-2.5 hover:brightness-110 hover:-translate-y-0.5 transition-transform shadow-md"
          >
            <Facebook size={17} fill="currentColor" />
            Facebook
          </a>
        </div>
      </section>

      {/* Témoignages */}
      <section className="mt-16" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-5 mb-6">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-kora-terracotta" />
            <h2 className="font-display font-bold text-lg text-kora-ink">Ils ont trouvé l'amour ou l'amitié</h2>
          </div>
          <p className="text-sm text-kora-ink/60 mt-1">Des milliers de membres de la diaspora nous font confiance.</p>
        </div>
        <div className="relative overflow-hidden" data-testid="testimonials-marquee">
          <div className="flex gap-4 w-max animate-marquee px-4">
            {[
              { name: "Sandra", city: "Paris 🇫🇷", photo: "https://images.unsplash.com/photo-1743871698163-a2e470d8eac7?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", text: "Grâce à KoraLove, j'ai rencontré un homme formidable qui partage mes valeurs. Aujourd'hui, on prépare notre mariage !" },
              { name: "Marc", city: "Bruxelles 🇧🇪", photo: "https://images.unsplash.com/photo-1522529599102-193c0d76b5b6", text: "Ivoirien vivant en Belgique, c'était dur de faire des rencontres sérieuses. KoraLove a tout changé pour moi." },
              { name: "Christelle", city: "Montréal 🇨🇦", photo: "https://images.unsplash.com/photo-1534470717-233b39a41c54?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", text: "J'ai d'abord trouvé une belle amitié, puis bien plus. La communauté est respectueuse et chaleureuse." },
              { name: "Ibrahim", city: "Londres 🇬🇧", photo: "https://images.unsplash.com/photo-1625181796571-7f0d4571ab12", text: "Simple et efficace. J'ai échangé avec des personnes qui partagent mes valeurs et ma culture." },
              { name: "Fatou", city: "New York 🇺🇸", photo: "https://images.unsplash.com/photo-1606416132922-22ab37c1231e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", text: "Enfin une plateforme qui comprend la diaspora africaine ! Je recommande à 100%." },
            ].concat([
              { name: "Sandra", city: "Paris 🇫🇷", photo: "https://images.unsplash.com/photo-1743871698163-a2e470d8eac7?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", text: "Grâce à KoraLove, j'ai rencontré un homme formidable qui partage mes valeurs. Aujourd'hui, on prépare notre mariage !" },
              { name: "Marc", city: "Bruxelles 🇧🇪", photo: "https://images.unsplash.com/photo-1522529599102-193c0d76b5b6", text: "Ivoirien vivant en Belgique, c'était dur de faire des rencontres sérieuses. KoraLove a tout changé pour moi." },
              { name: "Christelle", city: "Montréal 🇨🇦", photo: "https://images.unsplash.com/photo-1534470717-233b39a41c54?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", text: "J'ai d'abord trouvé une belle amitié, puis bien plus. La communauté est respectueuse et chaleureuse." },
              { name: "Ibrahim", city: "Londres 🇬🇧", photo: "https://images.unsplash.com/photo-1625181796571-7f0d4571ab12", text: "Simple et efficace. J'ai échangé avec des personnes qui partagent mes valeurs et ma culture." },
              { name: "Fatou", city: "New York 🇺🇸", photo: "https://images.unsplash.com/photo-1606416132922-22ab37c1231e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", text: "Enfin une plateforme qui comprend la diaspora africaine ! Je recommande à 100%." },
            ]).map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="shrink-0 w-80 bg-white rounded-3xl p-6 border border-kora-line shadow-[0_8px_30px_rgba(44,30,22,0.06)]">
                <div className="flex items-center gap-3">
                  <img src={t.photo} alt={t.name} className="w-14 h-14 rounded-full object-cover" draggable={false} />
                  <div>
                    <p className="font-display font-bold text-kora-ink">{t.name}</p>
                    <p className="text-xs text-kora-ink/60">{t.city}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mt-3 text-kora-ochre">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={15} fill="currentColor" />)}
                </div>
                <p className="text-sm text-kora-ink/75 mt-2 leading-relaxed">« {t.text} »</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-md md:max-w-3xl mx-auto px-6 mt-20" data-testid="landing-faq">
        <div className="text-center mb-8">
          <p className="inline-flex items-center gap-2 text-kora-terracotta font-bold text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-kora-terracotta" /> Questions fréquentes
          </p>
          <h2 className="font-display font-black text-2xl md:text-3xl text-kora-ink mt-1">Tout ce que vous devez savoir</h2>
        </div>
        <div className="space-y-3">
          {[
            { q: "KoraLove est-il gratuit\u00A0?", a: "Oui ! L'inscription et les fonctionnalités essentielles sont entièrement gratuites. L'abonnement Premium débloque les messages illimités, les appels vidéo et la liste de vos visiteurs." },
            { q: "À qui s'adresse KoraLove\u00A0?", a: "KoraLove est pensé pour la diaspora africaine en Europe, aux États-Unis, en Chine et au Canada. Vous filtrez par pays d'origine et de résidence pour rencontrer des personnes qui partagent votre culture." },
            { q: "Puis-je chercher une simple amitié\u00A0?", a: "Absolument. Dès l'inscription, vous choisissez votre intention : rencontre amicale ou rencontre en vue du mariage. Vous rencontrez ainsi des personnes avec le même objectif." },
            { q: "Comment ma sécurité est-elle assurée\u00A0?", a: "Les profils sont 100% vérifiés et vous disposez d'outils de signalement et de blocage d'un profil." },
            { q: "Comment fonctionne le matching\u00A0?", a: "Aimez les profils qui vous plaisent. Lorsque l'intérêt est réciproque, c'est un match et vous pouvez commencer à discuter en toute simplicité." },
          ].map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-2xl border border-kora-line overflow-hidden">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-display font-bold text-kora-ink">{f.q}</span>
                  <ChevronDown size={20} className={`shrink-0 text-kora-terracotta transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                </button>
                <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <p className="px-5 pb-4 text-sm text-kora-ink/70 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white mt-16" style={{ backgroundColor: "#3b82f6" }} data-testid="landing-footer">
        <div className="max-w-md md:max-w-5xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-kora-terracotta flex items-center justify-center">
                  <Heart size={16} className="text-white" fill="white" />
                </div>
                <span className="font-display font-bold text-xl">KoraLove</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">Le meilleur site de rencontre pour la diaspora africaine en Europe, aux USA, en Chine et au Canada. Amitié et mariage, en toute simplicité.</p>
              <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" data-testid="footer-playstore-badge" className="inline-block mt-4">
                <img src={PLAY_BADGE_FR} alt="Disponible sur Google Play" className="h-14" />
              </a>
              <p className="text-[11px] text-white/50 leading-relaxed mt-1">Utilisez la même adresse email Google que sur le site web pour retrouver votre compte dans l'application.</p>
              <div className="flex gap-3 mt-4">
                {[Facebook, Instagram, Twitter].map((Ic, i) => (
                  <a key={i} href="#" data-testid={`footer-social-${i}`} className="w-9 h-9 rounded-full bg-white/10 hover:bg-kora-terracotta transition-colors flex items-center justify-center">
                    <Ic size={16} />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <p className="font-display font-bold text-sm uppercase tracking-wider text-white/50 mb-3">Découvrir</p>
              <ul className="space-y-2 text-sm text-white/80">
                <li><button data-testid="footer-link-amicale" onClick={handleLogin} className="hover:text-kora-ochre transition-colors">Rencontre amicale</button></li>
                <li><button data-testid="footer-link-mariage" onClick={handleLogin} className="hover:text-kora-ochre transition-colors">Rencontre mariage</button></li>
                <li><button data-testid="footer-link-premium" onClick={handleLogin} className="hover:text-kora-ochre transition-colors">Premium</button></li>
              </ul>
            </div>

            <div>
              <p className="font-display font-bold text-sm uppercase tracking-wider text-white/50 mb-3">Société</p>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/a-propos" data-testid="footer-link-apropos" className="hover:text-kora-ochre transition-colors">À propos</Link></li>
                <li><Link to="/comment-ca-marche" data-testid="footer-link-comment" className="hover:text-kora-ochre transition-colors">Comment ça marche</Link></li>
                <li><Link to="/tarifs" data-testid="footer-link-tarifs" className="hover:text-kora-ochre transition-colors">Tarifs</Link></li>
                <li><Link to="/conseils-securite" data-testid="footer-link-securite" className="hover:text-kora-ochre transition-colors">Conseils sécurité</Link></li>
                <li><Link to="/blog" data-testid="footer-link-blog" className="hover:text-kora-ochre transition-colors">Blog</Link></li>
              </ul>
            </div>

            <div>
              <p className="font-display font-bold text-sm uppercase tracking-wider text-white/50 mb-3">Légal</p>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/conditions" data-testid="footer-link-conditions" className="hover:text-kora-ochre transition-colors">Conditions d'utilisation</Link></li>
                <li><Link to="/confidentialite" data-testid="footer-link-confidentialite" className="hover:text-kora-ochre transition-colors">Confidentialité</Link></li>
                <li><Link to="/politique-utilisation-acceptable" data-testid="footer-link-pua" className="hover:text-kora-ochre transition-colors">Politique d'utilisation acceptable (PUA)</Link></li>
                <li><Link to="/politique-remboursement" data-testid="footer-link-remboursement" className="hover:text-kora-ochre transition-colors">Politique de remboursement</Link></li>
                <li><Link to="/contact" data-testid="footer-contact" className="hover:text-kora-ochre transition-colors">Nous contacter</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/50">© {new Date().getFullYear()} KoraLove. Tous droits réservés.</p>
            <div className="flex items-center gap-4">
              <Link to="/demandes-legales" data-testid="footer-link-demandes-legales" className="text-xs text-white/40 hover:text-white/70 transition-colors">Demandes légales</Link>
              <p className="text-xs text-white/50">Fait avec ❤️ pour la diaspora africaine</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
